export type BrandingScreen =
  | "workspaces"
  | "studio"
  | "versions"
  | "targets"
  | "previews"
  | "agent-access"
  | "runtime-access"
  | "workspace-audit"
  | "system-brandings"
  | "organization-audit"
  | "new-workspace";

export interface BrandingLocation {
  readonly screen: BrandingScreen;
  readonly workspaceId?: string;
  readonly organizationId?: string;
}

export const workspaceScreenSegments: Readonly<Record<Exclude<BrandingScreen,
  "workspaces" | "system-brandings" | "organization-audit" | "new-workspace"
>, string>> = {
  studio: "branding/studio",
  versions: "branding/versions",
  targets: "delivery/targets",
  previews: "delivery/previews",
  "agent-access": "access/agents",
  "runtime-access": "access/runtime",
  "workspace-audit": "governance/audit",
};

export const brandingScreenLabels: Readonly<Record<BrandingScreen, string>> = {
  workspaces: "Workspaces",
  studio: "Brand Studio",
  versions: "Versions",
  targets: "Preview targets",
  previews: "Preview sessions",
  "agent-access": "Agent MCP",
  "runtime-access": "Runtime credentials",
  "workspace-audit": "Workspace audit",
  "system-brandings": "System branding library",
  "organization-audit": "Organization audit",
  "new-workspace": "New workspace",
};

export function workspacesPath(): string {
  return "/workspaces";
}

export function workspaceScreenPath(workspaceId: string, screen: keyof typeof workspaceScreenSegments): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/${workspaceScreenSegments[screen]}`;
}

export function systemBrandingsPath(workspaceId?: string): string {
  return withContext("/library/system-brandings", "workspace", workspaceId);
}

export function organizationAuditPath(organizationId: string, workspaceId?: string): string {
  return withContext(`/organizations/${encodeURIComponent(organizationId)}/audit`, "workspace", workspaceId);
}

export function matchBrandingLocation(pathname: string): BrandingLocation | null {
  if (/^\/workspaces\/?$/.test(pathname)) return { screen: "workspaces" };
  if (/^\/workspaces\/new\/?$/.test(pathname)) return { screen: "new-workspace" };
  if (/^\/library\/system-brandings\/?$/.test(pathname)) return { screen: "system-brandings" };

  const organizationMatch = pathname.match(/^\/organizations\/([^/]+)\/audit\/?$/);
  if (organizationMatch?.[1]) {
    const organizationId = decodeSegment(organizationMatch[1]);
    return organizationId ? { screen: "organization-audit", organizationId } : null;
  }

  const workspaceMatch = pathname.match(/^\/workspaces\/([^/]+)\/(.+?)\/?$/);
  if (!workspaceMatch?.[1] || !workspaceMatch[2]) return null;
  const workspaceId = decodeSegment(workspaceMatch[1]);
  if (!workspaceId) return null;
  const entry = Object.entries(workspaceScreenSegments).find(([, segment]) => segment === workspaceMatch[2]);
  return entry ? { screen: entry[0] as keyof typeof workspaceScreenSegments, workspaceId } : null;
}

export function workspaceScreenFromLocation(location: BrandingLocation): keyof typeof workspaceScreenSegments {
  return location.screen in workspaceScreenSegments
    ? location.screen as keyof typeof workspaceScreenSegments
    : "versions";
}

function withContext(path: string, key: string, value?: string): string {
  return value ? `${path}?${key}=${encodeURIComponent(value)}` : path;
}

function decodeSegment(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}
